import { Button } from "./ui/button";
import { PageLink } from "./PageLink";
import { Mark } from "./Mark";
import { PageHero, HeroLink } from "./PageHero";
import { RunConsole, EvalBoard, MentorChat, runs } from "./ProductVisuals";
import type { Accelerator } from "../data/accelerators";
import { ArrowRight, CheckCircle } from "lucide-react";
import type { ReactNode } from "react";

const visuals: Record<Accelerator["visual"], ReactNode> = {
  stageone: <RunConsole {...runs.assessment} />,
  learning: <MentorChat />,
  promptbench: <EvalBoard />,
};

/** Shared template for Accelerators & Tools (StageOne, Learning tools, PromptBench). */
export function AcceleratorPage({ data, onNavigate, onNavigateToDemo }: { data: Accelerator; onNavigate: (p: string) => void; onNavigateToDemo: () => void }) {
  return (
    <div className="min-h-screen bg-white">
      <PageHero
        crumb={<><span className="font-semibold text-royal-indigo">Accelerators &amp; Tools</span><span className="mx-1.5">/</span>{data.name}</>}
        title={data.headline}
        lede={data.subhead}
        actions={
          <>
            <Button variant="cta" size="cta" onClick={onNavigateToDemo}>Schedule a demo</Button>
            <HeroLink onClick={() => onNavigate("platform")}>How TurfAI works</HeroLink>
          </>
        }
        visual={visuals[data.visual]}
      />

      {/* How TurfAI powers it */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl">
            <h2 className="text-[28px] lg:text-[36px] leading-tight font-bold tracking-tight text-dark-indigo">{data.powersHeadline}</h2>
            <ol className="mt-8 grid gap-4 md:grid-cols-2">
              {data.powers.map((p, i) => (
                <li key={p} className="flex gap-4 rounded-lg border border-softblack-tint-20 bg-white p-5">
                  <span className="font-display text-[22px] font-bold leading-none text-royal-indigo">{i + 1}</span>
                  <span className="text-[16px] leading-relaxed text-soft-black">{p}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      {data.capabilities.length > 0 && (
        <section className="py-20 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="max-w-3xl text-[28px] lg:text-[36px] leading-tight font-bold tracking-tight text-dark-indigo">{data.capabilitiesHeadline}</h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {data.capabilities.map((c) => (
                <div key={c.title} className="rounded-lg border border-softblack-tint-20 p-6">
                  <h3 className="font-display text-[19px] font-semibold text-dark-indigo">{c.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-soft-black">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Products in the family (learning tools) */}
      {data.products && (
        <section className="py-20 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="max-w-3xl text-[28px] lg:text-[36px] leading-tight font-bold tracking-tight text-dark-indigo">{data.capabilitiesHeadline}</h2>
            <div className="mt-10 grid gap-6 lg:grid-cols-3">
              {data.products.map((p) => (
                <div key={p.name} id={p.name.toLowerCase()} className="rounded-lg border border-softblack-tint-20 p-6">
                  <h3 className="font-display text-[22px] font-semibold text-dark-indigo">{p.name}</h3>
                  <p className="mt-2 text-[16px] font-semibold text-royal-indigo">{p.line}</p>
                  <ul className="mt-5 space-y-2.5">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-2.5 text-[15px] text-soft-black">
                        <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-royal-indigo" />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                  {p.audience && <p className="mt-5 border-t border-softblack-tint-20 pt-4 text-[13px] text-soft-black">For: {p.audience}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Proof */}
      <section className="py-16 band-surface border-y border-softblack-tint-20">
        <div className="container mx-auto px-4">
          <p className="max-w-4xl font-display text-[24px] lg:text-[28px] font-semibold leading-snug text-dark-indigo">{data.proof}</p>
        </div>
      </section>

      {/* Related */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-[28px] lg:text-[36px] leading-tight font-bold tracking-tight text-dark-indigo mb-8">Related</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl">
            {data.related.map((r) => (
              <PageLink key={r.page} page={r.page} onNavigate={onNavigate} className="text-left p-6 rounded-lg border border-softblack-tint-20 hover:border-indigo-tint-50">
                <span className="font-semibold text-dark-indigo flex items-center justify-between">{r.label}<ArrowRight className="w-4 h-4 text-royal-indigo" /></span>
                <span className="mt-1 block text-sm text-soft-black">{r.desc}</span>
              </PageLink>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-dark-indigo text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-[32px] lg:text-[40px] leading-tight font-bold tracking-tight mb-6">See {data.name} on <Mark>your workflow</Mark></h2>
          <Button variant="onDark" size="lg" onClick={onNavigateToDemo}>Schedule a demo</Button>
        </div>
      </section>
    </div>
  );
}
